import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { useOutletContext, useNavigate, useParams } from 'react-router-dom'
import { useURLState } from '@/hooks/useURLState'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import DateDropdown from '@/components/dashboard/DateDropdown'
import { privatize } from '@/lib/utils/revenueUtils'
import { HugeiconsIcon } from "@hugeicons/react"
import { Alert02Icon, Loading01Icon } from "@hugeicons/core-free-icons"

import { useAttributionData } from '@/components/attribution/hooks/useAttributionData'
import { useAIContextSync } from '@/components/attribution/hooks/useAIContextSync'
import CollectedSplitCard from '@/components/attribution/CollectedSplitCard'
import { collectedSplit, renewalDollars, renewalsByCampaign as computeRenewalsByCampaign } from '@/components/attribution/collectedSplit'
import AttributionFunnel, { selectedCampaigns, type FunnelSelection } from '@/components/attribution/AttributionFunnel'
import { useAttributionFunnel } from '@/components/attribution/hooks/useAttributionFunnel'
import AttributionSetupWizard from '@/components/attribution/AttributionSetupWizard'
import { FeatureGate } from '@/components/shared/FeatureGate'
import AttributionModelPanel, { useAttributionModelsFlag } from '@/components/attribution/AttributionModelPanel'
import {
  AdsHeroCard,
  AdsNoStripeReport,
  AttributionTablesCard,
  GoogleAdsReconnectBanner,
  ReportActions,
  ReportHeader,
  StripeOnlyReport,
} from '@/components/attribution/AttributionReports'
import AttributionCampaigns from '@/components/attribution/AttributionCampaigns'
import type { GoalType } from '@/components/attribution/AttributionCampaigns'
import { Card } from '@/components/ui/card'
import {
  AttributionLoading,
  AttributionNoPayments,
  AttributionNothingConnected,
} from '@/components/attribution/AttributionEmptyStates'
import {
  adPlatformName,
  adsHeadline,
  adsMeta,
  adsNoRevenueHeadline,
  adsNoRevenueMeta,
  fmtMoney,
  periodPhrase,
  rangeDays,
  rangeLabel,
  selectAttributionView,
  stripeOnlyHeadline,
  stripeOnlyMeta,
  widerPeriod,
} from '@/components/attribution/attributionView'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DashboardContext {
  propertyId?: string
  token?: string
  period?: string
  filters?: any[]
  dashboardData?: any
  isLoading?: boolean
  showRevenue?: boolean
}

const PRIVACY_STORAGE_KEY = 'humblytics:attribution:privacy'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdsAttributionDashboard() {
  const ctx = useOutletContext<DashboardContext>()
  const params = useParams()
  const navigate = useNavigate()
  const { state: urlState, updateState } = useURLState()
  const dashboardData = ctx?.dashboardData
  const isLoading = ctx?.isLoading || false
  const propertyId = ctx?.propertyId
  const period = urlState.period

  // Base path helper
  const getBasePath = () => {
    if (params.magicId && params.password) return `/s/${params.magicId}/${params.password}`
    if (params.workspaceId) return `/workspace/${params.workspaceId}`
    if (params.propertySlugId) return `/p/${params.propertySlugId}`
    return ''
  }
  const basePath = getBasePath()

  // All data via hooks
  const data = useAttributionData(propertyId, period, dashboardData, isLoading)
  useAIContextSync(data, isLoading)

  const {
    metaConnections, googleAdsConnections, connectorType, isLoadingRevenue,
    adTotals, totalRevenueDollars, attributedRevenueDollars,
    trueROAS, totalTrialCount, adRevenueDollars, adPaymentCount,
    classified, humblyticsData,
    landingPagesWithRevenue, chartData,
    isLoadingMeta, isLoadingGoogleAds, isLoadingRevenueMetrics, metaError,
    hasAdData, adsAttributionData, isLoadingAdsAttribution, adsAttributionError,
  } = data

  // "From visit to payment": everyone, narrowed by the page filters and by an
  // ad selection. A selected campaign that left the range falls back to everyone.
  const filters = urlState.filters ?? []
  const filtersActive = filters.length > 0
  const matchedCampaigns = adsAttributionData?.campaigns ?? []
  const [funnelPick, setFunnelPick] = useState<FunnelSelection>({ kind: 'everyone' })
  const funnelSelection: FunnelSelection =
    funnelPick.kind === 'campaign' && !matchedCampaigns.some((c) => c.utm_campaign === funnelPick.utmCampaign)
      ? { kind: 'everyone' }
      : funnelPick
  const funnelUtmCampaigns = useMemo(
    () => selectedCampaigns(matchedCampaigns, funnelSelection)?.map((c) => c.utm_campaign) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adsAttributionData, funnelSelection.kind, funnelSelection.kind === 'campaign' ? funnelSelection.utmCampaign : funnelSelection.kind === 'ads' ? funnelSelection.platform : null],
  )
  const funnel = useAttributionFunnel(propertyId, data.dateRange, filters, funnelUtmCampaigns)

  const showAttributionModels = useAttributionModelsFlag()
  const [goal, setGoal] = useState<GoalType>('revenue')
  const [privacyMode, setPrivacyMode] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(PRIVACY_STORAGE_KEY) === 'true'
  })
  const [pickDatesOpen, setPickDatesOpen] = useState(false)
  const adFetchStartedRef = useRef(false)

  useEffect(() => {
    if (isLoadingMeta || isLoadingGoogleAds) {
      adFetchStartedRef.current = true
    }
  }, [isLoadingMeta, isLoadingGoogleAds])

  const attributionSettled = Boolean(
    propertyId &&
    !isLoadingRevenue &&
    !isLoadingRevenueMetrics &&
    adFetchStartedRef.current &&
    !isLoadingMeta &&
    !isLoadingGoogleAds &&
    !isLoadingAdsAttribution
  )

  useEffect(() => {
    window.localStorage.setItem(PRIVACY_STORAGE_KEY, String(privacyMode))
  }, [privacyMode])

  const view = selectAttributionView({
    settled: attributionSettled && !(isLoading && !dashboardData),
    hasAds: hasAdData,
    revenueConfigured: data.isRevenueConfigured,
    paymentCount: data.paymentCount,
    revenueDollars: totalRevenueDollars,
  })

  const provider = connectorType === 'foxy' ? 'Foxy' : 'Stripe'
  const platform = adPlatformName(data.adSpendByPlatform.meta, data.adSpendByPlatform.google)
    ?? (metaConnections.length > 0 && googleAdsConnections.length === 0 ? 'Meta'
      : googleAdsConnections.length > 0 && metaConnections.length === 0 ? 'Google' : null)
  const range = rangeLabel(data.dateRange)
  const money = (v: number) => privatize(fmtMoney(v), privacyMode)
  const goConnectors = () => navigate(`${basePath}/connectors`)

  const actions = (
    <ReportActions
      privacyMode={privacyMode}
      onTogglePrivacy={() => setPrivacyMode((value) => !value)}
      onConnections={goConnectors}
    />
  )

  const googleHealth = adsAttributionData?.googleAdsHealth
  // Live Google spend for this range loaded, so the connection works: the
  // server's health flag can lag a sync and would claim spend is missing.
  const googleSpendLoaded = data.adSpendByPlatform.google > 0
  const googleBanner = googleHealth && googleHealth !== 'ok' && googleAdsConnections.length > 0 && !googleSpendLoaded ? (
    <GoogleAdsReconnectBanner
      health={googleHealth}
      otherPlatformsOk={metaConnections.length > 0 && !metaError}
      onReconnect={goConnectors}
    />
  ) : null

  // A failed campaign join is not "no ads": say the numbers are partial.
  const adsLoadError = hasAdData && adsAttributionError
    ? "Campaign matching couldn't be loaded, so Earned uses paid UTM visits only"
    : null
  const errorAlert = (metaError || adsLoadError) ? (
    <Alert className="rounded-lg border-border/40 bg-muted/20 py-3 [&>svg]:text-warning dark:[&>svg]:text-warning">
      <HugeiconsIcon icon={Alert02Icon} size={16} />
      <AlertTitle className="text-sm font-medium text-foreground">
        {metaError ? "Some ad data couldn't be loaded" : 'Data may be incomplete'}
      </AlertTitle>
      <AlertDescription className="text-xs text-muted-foreground">
        {metaError || adsLoadError}. Try changing the time period or refreshing.
      </AlertDescription>
    </Alert>
  ) : null

  const updating = isLoading && dashboardData ? (
    <div className="rounded-md border border-border/30 bg-muted/20 px-4 py-2.5 flex items-center gap-2.5 animate-pulse">
      <div className="h-3.5 w-3.5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      <span className="text-xs text-muted-foreground">Updating data…</span>
    </div>
  ) : null

  const funnelCard = (needsRevenue: string | null) => (
    <AttributionFunnel
      data={funnel.data}
      isLoading={funnel.isLoading}
      error={funnel.error}
      campaigns={matchedCampaigns}
      selection={funnelSelection}
      onSelectionChange={setFunnelPick}
      filtersActive={filtersActive}
      provider={provider}
      needsRevenue={needsRevenue}
      privacyMode={privacyMode}
      goal={goal}
    />
  )

  // Filters narrow visits, so they reach the funnel and the traffic tables.
  // Ad spend and revenue totals stay whole: a platform reports no split by
  // country or page, and a filtered return per $1 would divide part of the
  // revenue by all of the spend.
  const filtersNote = filtersActive ? (
    <p className="text-xs leading-4 text-muted-foreground">
      Filters apply to visitors, the funnel and the Landing pages and Sources tables. Spend, revenue totals and campaigns cover all traffic.
    </p>
  ) : null

  const landingPageGoals = funnel.data?.landing_page_goals ?? {}

  const tablesCard = (needsRevenue: string | null, showCampaigns = true) => (
    <AttributionTablesCard
      showCampaigns={showCampaigns && (hasAdData || isLoadingMeta || isLoadingGoogleAds)}
      isLoadingAds={isLoadingMeta || isLoadingGoogleAds || isLoadingAdsAttribution}
      adsAttributionData={adsAttributionData}
      adsAttributionError={adsAttributionError}
      sourceData={humblyticsData.sourceData}
      landingPages={landingPagesWithRevenue}
      connectorType={connectorType}
      transactions={classified.attributed}
      basePath={basePath}
      navigate={navigate}
      privacyMode={privacyMode}
      needsRevenue={needsRevenue}
      goal={goal}
      landingPageGoals={landingPageGoals}
    />
  )

  // Paper's report ends at the ad-to-payment strip; trials and payments are
  // in the report itself. Only the flagged attribution-model panel follows.
  const revenueBreakdown = showAttributionModels && propertyId ? (
    <AttributionModelPanel propertyId={propertyId} period={period ?? 'LAST_30_DAYS'} />
  ) : null

  const shell = (children: ReactNode) => (
    <FeatureGate feature="revenueAttribution">
      <TooltipProvider>
        <div className="w-full space-y-6">{children}</div>
      </TooltipProvider>
    </FeatureGate>
  )

  if (view === 'loading') {
    return shell(
      <AttributionLoading
        banner={googleBanner}
        spentLabel={platform ? `Spent (${platform})` : 'Spent (ads)'}
        earnedLabel={`Earned (${provider})`}
      />,
    )
  }

  if (view === 'nothing-connected') {
    return shell(
      <AttributionNothingConnected
        onConnectStripe={goConnectors}
        onConnectMeta={goConnectors}
        onConnectGoogle={goConnectors}
      />,
    )
  }

  if (view === 'ads-no-stripe') {
    const totals = adsAttributionData?.totals
    const landed = totals?.sessions ?? 0
    const clicks = totals?.clicks ?? adTotals.clicks
    // Server visits and clicks cover matched campaigns only: cost them against
    // those campaigns' spend, not spend no visit arrived from.
    const landedSpend = totals ? (totals.matched_spend ?? adTotals.spend) : adTotals.spend
    const days = rangeDays(data.dateRange)
    return shell(
      <>
        {updating}
        {errorAlert}
        <AdsNoStripeReport
          provider={provider}
          platform={platform}
          title={adsNoRevenueHeadline(platform, landed, landedSpend, provider, money)}
          meta={adsNoRevenueMeta(range, landedSpend, clicks, landed, money)}
          spend={adTotals.spend}
          perDay={days > 1 ? adTotals.spend / days : null}
          landed={landed}
          chartData={chartData}
          banner={<>{filtersNote}{googleBanner}</>}
          tables={tablesCard(provider)}
          funnel={funnelCard(provider)}
          privacyMode={privacyMode}
          onConnectRevenue={goConnectors}
          actions={actions}
        />
      </>,
    )
  }

  if (view === 'no-payments') {
    const wider = widerPeriod(period)
    const days = rangeDays(data.dateRange)
    return shell(
      <>
        {updating}
        {errorAlert}
        {googleBanner}
        <AttributionNoPayments
          phrase={periodPhrase(period)}
          meta={[
            `${provider} is connected`,
            hasAdData && adTotals.spend > 0 ? `${money(adTotals.spend)} of ${platform ? `${platform} ` : 'ad '}spend in this range` : null,
          ].filter(Boolean).join(' · ')}
          hasAds={hasAdData}
          spend={adTotals.spend}
          trials={data.trialCountAll}
          privacyMode={privacyMode}
          showShortRangeHint={days <= 7}
          wider={wider ? { label: wider.label, onSelect: () => updateState({ period: wider.period }) } : null}
          pickDates={
            <DateDropdown
              period={period}
              onDateChange={(p) => updateState({ period: p })}
              triggerLabel="Pick dates"
              customOnly
              open={pickDatesOpen}
              onOpenChange={setPickDatesOpen}
              triggerClassName="h-8 px-3 text-[13px]"
            />
          }
          actions={actions}
        />
      </>,
    )
  }

  if (view === 'stripe-only') {
    const rows = data.sourceRevenueRows
    return shell(
      <>
        {updating}
        {errorAlert}
        {filtersNote}
        <StripeOnlyReport
          provider={provider}
          title={stripeOnlyHeadline(rows, totalRevenueDollars, money)}
          meta={stripeOnlyMeta(data.paymentCount, provider, range)}
          earned={totalRevenueDollars}
          customers={data.customerCount != null
            ? { label: 'Paying customers', value: data.customerCount }
            : { label: 'Payments', value: data.paymentCount }}
          trials={data.trialCountAll}
          chartData={chartData}
          sources={rows}
          funnel={funnelCard(null)}
          privacyMode={privacyMode}
          onConnectAds={goConnectors}
          actions={actions}
        />
        {revenueBreakdown}
      </>,
    )
  }

  // Ads and revenue: did the money come back
  // Earned = payments joined to an ad campaign. Revenue matched to organic,
  // direct or referral visits is shown beside it, never divided by ad spend.
  const renewalsByCampaign = renewalDollars(classified.attributed) !== null
    ? computeRenewalsByCampaign(classified.attributed)
    : null

  const adDollars = Math.max(0, adRevenueDollars)
  // A renewal pays the campaign the subscription first came from: it is
  // collected, but not new return on ads. Known once payments carry Stripe's
  // billing_reason; until then every ad-credited payment counts as earned.
  const renewalsFromAds = renewalDollars(classified.attributed)
  const earnedDollars = Math.max(0, adDollars - Math.min(renewalsFromAds ?? 0, adDollars))
  // One split for the headline, the warning and the card: money matched to a
  // non-ad visit and money matched to no visit are different problems.
  const split = collectedSplit({
    collected: totalRevenueDollars,
    attributed: attributedRevenueDollars,
    ads: adDollars,
    renewals: renewalsFromAds,
  })
  const paid = adPaymentCount
  const roasShown = renewalsFromAds != null && adTotals.spend > 0 ? earnedDollars / adTotals.spend : trueROAS

  return shell(
    <>
      {updating}
      {errorAlert}

      {/* Compact setup banner only when revenue connector is missing */}
      {!data.isRevenueConfigured && (
        <AttributionSetupWizard
          isTrackingActive={!!dashboardData}
          isRevenueConfigured={data.isRevenueConfigured}
          connectorType={connectorType}
          metaConnectionCount={metaConnections.length}
          basePath={basePath}
          navigate={navigate}
          variant="compact"
        />
      )}

      <div className="space-y-4">
        {/* 1. Hero card: verdict + chart + goal selector */}
        <AdsHeroCard
          platform={platform}
          earnedDollars={earnedDollars}
          spend={adTotals.spend}
          roas={roasShown}
          totalTrialCount={totalTrialCount}
          adSessions={adsAttributionData?.totals?.sessions ?? 0}
          adFormSubmits={adsAttributionData?.totals?.form_submissions ?? 0}
          chartData={chartData}
          earnedKey="adRevenue"
          earnedLabel={renewalsFromAds != null ? `New purchases from ad clicks (${provider})` : `Earned from ad clicks (${provider})`}
          spentLabel={platform ? `Spent (${platform})` : 'Spent (ads)'}
          provider={provider}
          goal={goal}
          onGoalChange={setGoal}
          privacyMode={privacyMode}
          avgHoursToConvert={adsAttributionData?.totals?.avg_hours_to_convert}
          timeToConvertSampleSize={adsAttributionData?.totals?.time_to_convert_sample_size}
          loadingNote={isLoadingRevenueMetrics ? (
            <span className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin shrink-0" />
              Revenue loading…
            </span>
          ) : null}
          warning={adDollars === 0 && totalRevenueDollars > 0 && adTotals.spend > 0 ? (
            <div className="flex gap-3 rounded-lg border border-warning/20 bg-warning-tint/40 px-4 py-3">
              <HugeiconsIcon icon={Alert02Icon} size={14} className="mt-0.5 shrink-0 text-warning" />
              <p className="text-xs leading-5 text-foreground">
                <span className="font-medium">
                  {split.notMatched > 0
                    ? `${money(totalRevenueDollars)} was collected but not matched to any visit.`
                    : `${money(totalRevenueDollars)} matched organic, direct or referral visits — not ad clicks.`}
                </span>
                {' '}{split.notMatched > 0 && connectorType === 'stripe'
                  ? 'To link payments, add ?stripe_session_id={CHECKOUT_SESSION_ID} to your Stripe success URL. '
                  : ''}
                Check that each ad URL carries utm_campaign.
              </p>
            </div>
          ) : null}
        />

        {/* 2. Platform-level banners */}
        {googleBanner}
        {filtersNote}

        <div className="well flex flex-col gap-2">
          {/* 3. Campaign cards — standalone (goal controlled by hero card above) */}
          <Card className="gap-0 overflow-hidden py-0">
            <AttributionCampaigns
              adsAttributionData={adsAttributionData}
              isLoading={isLoadingMeta || isLoadingGoogleAds || isLoadingAdsAttribution}
              error={adsAttributionError}
              privacyMode={privacyMode}
              needsRevenue={null}
              renewalsByCampaign={renewalsByCampaign}
              goal={goal}
              onGoalChange={setGoal}
            />
          </Card>

          {/* 4. Collected vs attributed */}
          <CollectedSplitCard
            split={split}
            provider={provider}
            privacyMode={privacyMode}
          />

          {/* 5. Landing pages + Sources (no campaigns tab — they live above) */}
          {tablesCard(null, false)}

          {/* 6. Funnel */}
          {funnelCard(null)}
        </div>
      </div>

      {revenueBreakdown}
    </>,
  )
}
